import React from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { Typography } from "@mui/material";
import ShipeeziNewGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import AutoSuggest from "../../../../../../components/Form/AutoSuggest";
import { Color } from "../../../../../../components/Form/Button";
import Select from "../../../../../../components/Form/Select";
import { Filters } from "../../../../../../models/shipeezi.table";
import { StorageBox } from "../../../../../../models/storage.box";
import { User } from "../../../../../../models/user";
import { DatePeriodOptions } from "../../../../../../util/enums";
import { useActions } from "./hooks/useActions";
import {
  DownloadButton,
  EmptyDiv,
  ExportCSVButton,
  LeftActionsGrid,
  RightActionsGrid,
} from "./styles";
import { colors } from "@zapalacorp/tokens";
import { DocumentDownload } from "iconsax-react";
import { DefaultTooltip } from "Components/Generic/Tooltip";
import { handleGenerateMovementsAndLocationStatusCSV } from "Util/generateCsv";
import { useAppState } from "Context/StateContext";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";

const downloadSvg = require("Assets/img/document-download-outline.svg");

export interface DateOption {
  name: string;
  column: string;
}

export const MovementsReportsScreen = () => {
  const {
    getMovements,
    getObjects,
    getStorageBoxTypesList,
    getUsersList,
    getWarehousesList,
    movementsFilters,
    movementsReports,
    objectSelected,
    page,
    pageSize,
    selectedWarehouseId,
    setEndDate,
    setFilters,
    setObjectSelected,
    setPage,
    setPageSize,
    setSelectedWarehouseId,
    setStartDate,
    storageBoxTypesList,
    total,
    useExportMovementsCSVData,
    useGenerateMovementsReport,
    usersList,
    warehousesList,
  } = useActions();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const { isFetching: isFetchingWarehouses } = getWarehousesList();
  const { isFetching: loadingMovements } = getMovements();
  const { mutate: generateReport, isLoading: isGeneratingReport } = useGenerateMovementsReport();

  const { mutate: exportMovementsData, isLoading: isExportingCSV } = useExportMovementsCSVData(
    (data) =>
      handleGenerateMovementsAndLocationStatusCSV({
        data,
        reportName: "Movements_report",
        companyName: currentUser?.company.name || "",
      })
  );

  getStorageBoxTypesList();
  getUsersList();

  const renderDownloadReportButton = () => {
    return (
      <RightActionsGrid
        container
        item
        direction="row"
        alignItems="flex-end"
        justifyContent="flex-end"
      >
        <Grid item>
          <DownloadButton
            onClick={() => generateReport(movementsFilters)}
            customColor={Color.SECONDARY}
            disabled={
              !selectedWarehouseId ||
              isGeneratingReport ||
              loadingMovements ||
              movementsReports.length === 0
            }
            loading={isGeneratingReport}
            startIcon={<img src={downloadSvg} />}
          >
            {translate("downloadReport")}
          </DownloadButton>
        </Grid>
      </RightActionsGrid>
    );
  };

  const renderExportCSVButton = () => {
    return (
      <RightActionsGrid
        container
        item
        direction="row"
        alignItems="flex-end"
        justifyContent="flex-end"
      >
        <DefaultTooltip title={translate("exportCSV")} placement="top">
          <Grid item>
            <ExportCSVButton
              loading={isExportingCSV}
              customColor={Color.SECONDARY}
              disabled={isExportingCSV || loadingMovements || movementsReports.length === 0}
              onClick={() => exportMovementsData()}
              startIcon={<DocumentDownload size={20} color={colors.white} />}
            >
              {translate("exportCSV")}
            </ExportCSVButton>
          </Grid>
        </DefaultTooltip>
      </RightActionsGrid>
    );
  };

  const getColumns = () => {
    const columns: Column[] = [
      {
        name: "storageBox",
        hideSearch: true,
        title: translate("storageBoxType"),
        formatText: (storageBox: StorageBox) => storageBox?.storageBoxType?.name ?? "",
      },
      {
        name: "objectNumber",
        hideSearch: true,
        title: `${translate("object")} #`,
      },
      {
        name: "mapCellNameFrom",
        hideSearch: true,
        title: translate("from"),
      },
      {
        name: "mapCellNameTo",
        hideSearch: true,
        title: translate("to"),
      },
      {
        name: "createdAt",
        title: translate("when"),
        hideSearch: true,
        formatComponent: (date?: Date) => {
          if (date && currentUser?.preferences) {
            return formatDateWithUserPref(date, currentUser.preferences, "dateOnly");
          }

          return null;
        },
      },
      {
        name: "user",
        hideSearch: true,
        title: translate("byWhom"),
        formatText: (user?: User) => user?.fullName || "",
      },
    ];

    return columns;
  };

  const renderEmptyDiv = () => {
    return (
      <EmptyDiv>
        <Typography
          fontFamily="Manrope"
          fontWeight={400}
          fontSize="16px"
          color="#9CA6AF"
          lineHeight="18px"
          marginBottom="28px"
        >
          {translate("itFeelsEmptyRightNow")}
        </Typography>
      </EmptyDiv>
    );
  };

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const renderWarehouseSelect = () => {
    return (
      <LeftActionsGrid container item direction="row">
        <Select
          placeholder={`${translate("select")}...`}
          label={translate("warehouse")}
          disabled={isFetchingWarehouses || isGeneratingReport}
          value={selectedWarehouseId}
          onChange={(event: SelectChangeEvent<any>) => {
            const warehouseId = Number(event.target.value);

            const foundWarehouse = warehousesList?.find(
              (warehouse) => warehouse?.id === warehouseId
            );

            if (foundWarehouse && foundWarehouse.id) {
              setPage(0);
              setSelectedWarehouseId(foundWarehouse.id);
            }
          }}
        >
          {warehousesList?.map((warehouse) => (
            <MenuItem value={warehouse?.id} key={warehouse?.id}>
              {warehouse?.name}
            </MenuItem>
          ))}
        </Select>
      </LeftActionsGrid>
    );
  };

  const renderObjectSelect = () => {
    return (
      <LeftActionsGrid container item direction="row">
        <AutoSuggest
          label={`${translate("object")} #`}
          placeholder={translate("search")}
          noOptionsText={translate("noResults")}
          value={objectSelected || null}
          getOptionLabel={(option: StorageBox) => {
            if (!option?.number) return "";

            return option.number;
          }}
          getOptionSelected={(option: StorageBox, value: StorageBox) => option?.id === value?.id}
          filterOptions={(options) => options}
          inputValue={objectSelected?.number}
          clearOptionsOnEscape
          disabled={!selectedWarehouseId || isGeneratingReport}
          onChange={(value: StorageBox) => {
            setPage(0);
            setObjectSelected(value);
          }}
          onSearch={(value) => {
            return getObjects(value);
          }}
        />
      </LeftActionsGrid>
    );
  };

  const setStartEndDates = (period: string, dateFilters: Filters) => {
    switch (period) {
      case DatePeriodOptions.LAST_DAY:
        setStartDate(format(new Date(), "yyyy-MM-dd"));
        setEndDate(format(new Date(), "yyyy-MM-dd"));
        break;
      case DatePeriodOptions.LAST_WEEK:
        setStartDate(format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"));
        setEndDate(format(new Date(), "yyyy-MM-dd"));
        break;
      case DatePeriodOptions.LAST_MONTH:
        setStartDate(format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"));
        setEndDate(format(new Date(), "yyyy-MM-dd"));
        break;
      case DatePeriodOptions.LAST_15_DAYS:
        setStartDate(format(new Date(Date.now() - 15 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"));
        setEndDate(format(new Date(), "yyyy-MM-dd"));
        break;
      case DatePeriodOptions.CUSTOM_DATE:
        setStartDate(dateFilters?.date?.[0] || "");
        setEndDate(dateFilters?.date?.[0] || "");
        break;
      case DatePeriodOptions.CUSTOM_PERIOD:
        setStartDate(dateFilters?.dateRange?.[0] || "");
        setEndDate(dateFilters?.dateRange?.[1] || "");
        break;
      default:
        setStartDate("");
        setEndDate("");
    }
  };

  return (
    <ShipeeziNewGrid
      loadingData={loadingMovements}
      emptyData={() => renderEmptyDiv()}
      data={movementsReports}
      onChangePageSize={handlePageSize}
      onNextPage={handleCurrentPage}
      onPreviousPage={handleCurrentPage}
      onGoToPage={handleCurrentPage}
      columns={getColumns()}
      totalItems={total}
      pageSize={pageSize}
      pageSizeList={[20, 40, 60, 100]}
      currentPage={page}
      positionActionsColumn="right"
      showSelection={false}
      generalActions={{
        orderBy: {
          right: ["downloadReport", "exportCSV"],
          left: [
            "handleSelectDate",
            "handleSelectWarehouse",
            "handleSelectObject",
            "handleFilterBy",
          ],
        },
        handleFilterBy: {
          categories: [
            {
              name: translate("storageBoxType"),
              column: "storageBoxType",
              options: storageBoxTypesList?.map((type) => type?.name ?? ""),
            },
            {
              name: translate("byWhom"),
              column: "user",
              options: usersList?.map((user) => user?.fullName ?? ""),
            },
          ],
          onChange: handleFilter,
        },
        handleSelectDate: {
          onChange: (dateFilters, _, period) => {
            setStartEndDates(period, dateFilters);
            setPage(0);
          },
        },
        downloadReport: renderDownloadReportButton(),
        handleSelectWarehouse: renderWarehouseSelect(),
        handleSelectObject: renderObjectSelect(),
        exportCSV: renderExportCSVButton(),
      }}
    />
  );
};
